/**
 * Slice 58 — the one-page job report. Replaces the Job evidence and Check unit split.
 * Header → Photos by room → Unit items check (exceptions first) → Maintenance → Inventory (only when
 * low) → rating. Items count as Here unless marked, or unless a cleaner's flag on them is still open.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ChevronLeft, Loader2 } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { usePropertyRooms } from "@/hooks/usePropertyRooms";
import { useRoomCoverData } from "@/hooks/useRoomCoverPhotos";
import { useUnitItems, type PropertyUnitItem } from "@/hooks/useUnitItems";
import { usePhotoGroups } from "@/hooks/usePhotoGroups";
import { useInventoryRooms } from "@/hooks/useInventoryRooms";
import { useAppliances } from "@/hooks/useAppliances";
import { ReportPairs, type PairItem, type PairRoom, type PairStatus } from "@/components/owner/ReportPairs";
import { useUnitCheckMarks, useUpsertUnitCheckMark, syncUnitCheckIssueReport } from "@/hooks/useUnitCheckMarks";
import { useMaintenanceReportsForTask } from "@/hooks/useMaintenanceReportsForTask";
import { useCreateMaintenanceReport } from "@/hooks/useMaintenanceReports";
import { sortRoomsWalkOrder } from "@/lib/roomCoverPhotos";
import { sortUnitItems } from "@/lib/unitItems";
import { collectSimplePhotoUrls, readSimpleFlowState, type RoomShots, type RoomSkipReason } from "@/lib/simpleTurnover";
import { WHOLE_ROOM_KEY, groupsForRoom, itemLabel, itemsCountText, sameRoom, type PhotoGroup } from "@/lib/photoGroups";
import {
  isCleanerFlag,
  isFlagOpen,
  itemStatus,
  lowFromSubmission,
  openFlagByItem,
  reportCounts,
  submissionForTask,
  verdictLine,
  OWNER_CHECK_LIST_NAME,
  type ItemStatus,
  type SubmissionRow,
} from "@/lib/jobReport";
import {
  formatDurationWords,
  minutesBetween,
  stampDistanceWords,
  stampMapUrl,
} from "@/lib/locationStamps";
import { fetchStampSettings } from "@/lib/stampOutbox";
import { deleteTestClean, testSetupLine } from "@/lib/testClean";
import CleanerRatingCard from "@/components/owner/CleanerRatingCard";
import { ShareWithGuest } from "@/components/owner/ShareWithGuest";
import { ArrivalAnswersSection } from "@/components/owner/ArrivalAnswersSection";
import type { JobEvidenceTask } from "@/components/owner/JobEvidenceView";
import {
  ExceptionRow,
  LowInventory,
  MapTile,
  Pill,
  RoomBlock,
  SectionCard,
  StatusButtons,
  VerdictLine,
  type ExceptionRowModel,
  type RoomBlockModel,
} from "@/components/owner/JobReportParts";

interface Props {
  propertyId: string;
  propertyLabel: string;
  submittedAt: string;
  cleanerName: string;
  cleaningTask: JobEvidenceTask | null;
  cleaningTaskId: string | null;
  onBack: () => void;
  onGoToInventory?: () => void;
  onTestDeleted?: () => void;
}

const SKIP_TEXT: Record<RoomSkipReason, string> = {
  locked: "Skipped · door locked",
  guest: "Skipped · guest inside",
  unsafe: "Skipped · not safe",
};

const hhmm = (iso?: string | null) => {
  if (!iso) return "";
  try {
    return format(new Date(iso), "HH:mm");
  } catch {
    return "";
  }
};

type Room = { id: string; name: string };

export function JobReportView({
  propertyId,
  propertyLabel,
  submittedAt,
  cleanerName,
  cleaningTask,
  cleaningTaskId,
  onBack,
  onGoToInventory,
  onTestDeleted,
}: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isTest = !!cleaningTask?.is_test;
  const simple = readSimpleFlowState(cleaningTask?.entry_walkthrough_state);

  // Photos still on the cleaner's phone: check back quietly and fill them in.
  const { data: polledPending } = useQuery({
    queryKey: ["photos-pending", cleaningTaskId],
    queryFn: async () => {
      const { data } = await supabase.from("cleaning_tasks").select("photos_pending").eq("id", cleaningTaskId as string).maybeSingle();
      return (data as { photos_pending?: number | null } | null)?.photos_pending ?? 0;
    },
    enabled: !!cleaningTaskId && !!cleaningTask?.photos_pending,
    refetchInterval: (q) => ((q.state.data ?? 1) > 0 ? 8000 : false),
    initialData: cleaningTask?.photos_pending ?? 0,
  });
  const photosPending = polledPending ?? cleaningTask?.photos_pending ?? 0;

  const { data: roomRows = [] } = usePropertyRooms(propertyId);
  const { data: roomCover = {} } = useRoomCoverData(propertyId);
  const { data: itemsRaw = [] } = useUnitItems(propertyId);
  const { data: groups = [] } = usePhotoGroups(propertyId);
  const { data: invRoomsRaw = [] } = useInventoryRooms(propertyId);
  const { data: appliancesRaw = [] } = useAppliances(propertyId);
  const { data: marks = [] } = useUnitCheckMarks(cleaningTaskId ?? undefined);
  const { data: reports = [] } = useMaintenanceReportsForTask(cleaningTaskId ?? undefined);
  const upsert = useUpsertUnitCheckMark();
  const createReport = useCreateMaintenanceReport();

  const { data: stampSettings } = useQuery({
    queryKey: ["stamp-settings", propertyId],
    queryFn: () => fetchStampSettings(propertyId),
    staleTime: 60_000,
  });
  const { data: invItems = [] } = useQuery({
    queryKey: ["report-inventory-items", propertyId],
    queryFn: async () => {
      const { data } = await supabase
        .from("inventory_items")
        .select("id, item_name, quantity_type, threshold_count, threshold_value, room_id, photo_group_id")
        .eq("property_id", propertyId);
      return (data ?? []) as unknown as {
        id: string;
        item_name: string;
        quantity_type: string;
        threshold_count: number | null;
        threshold_value: number | null;
        room_id: string | null;
        photo_group_id: string | null;
      }[];
    },
    staleTime: 60_000,
  });
  const { data: submissions = [] } = useQuery({
    queryKey: ["report-inventory-submissions", propertyId],
    queryFn: async () => {
      const { data } = await supabase
        .from("inventory_submissions")
        .select("cleaner_id, submitted_at, items")
        .eq("property_id", propertyId)
        .order("submitted_at", { ascending: false })
        .limit(60);
      return (data ?? []) as SubmissionRow[];
    },
    staleTime: 60_000,
  });

  const rooms: Room[] = useMemo(
    () => sortRoomsWalkOrder(roomRows.filter((r) => !r.archived_at), roomCover).map((r) => ({ id: r.id, name: r.name })),
    [roomRows, roomCover],
  );
  const items = useMemo(() => sortUnitItems(itemsRaw), [itemsRaw]);
  const roomOf = (it: PropertyUnitItem): Room | null => rooms.find((r) => sameRoom(it.room, r.name)) ?? null;

  const flagByItem = useMemo(() => openFlagByItem(reports), [reports]);
  const statusOf = (id: string): ItemStatus => itemStatus(id, marks, flagByItem);
  const lowRows = useMemo(() => {
    if (!cleaningTask) return [];
    const sub = submissionForTask(submissions, cleaningTask);
    return sub ? lowFromSubmission(sub.items, invItems) : [];
  }, [submissions, cleaningTask, invItems]);

  const counts = reportCounts({
    itemIds: items.map((i) => i.id),
    marks,
    flags: reports,
    entryWalkthroughState: cleaningTask?.entry_walkthrough_state,
    lowCount: lowRows.length,
  });

  // ---------- header ----------
  const startedAt = cleaningTask?.started_at ?? null;
  const finishedAt = cleaningTask?.completed_at ?? null;
  const tookMin = startedAt && finishedAt ? minutesBetween(startedAt, finishedAt) : null;
  const awayM = stampSettings?.awayDistanceM ?? 300;
  const loc = (which: "start" | "finish") => {
    const t = (cleaningTask ?? {}) as Record<string, unknown>;
    const lat = t[`${which}_location_lat`] as number | null | undefined;
    const lng = t[`${which}_location_lng`] as number | null | undefined;
    const dist = (t[`${which}_location_distance_m`] as number | null | undefined) ?? null;
    const verified = (t[`${which}_location_verified`] as boolean | null | undefined) ?? null;
    if (lat == null || lng == null) return { text: "Location not saved", tone: "amber" as const, href: null, lat: null, lng: null };
    const away = verified != null ? !verified : dist != null ? dist > awayM : false;
    return {
      text: away ? stampDistanceWords(dist, true) ?? "Away from the property" : "At the property",
      tone: away ? ("red" as const) : ("green" as const),
      href: stampMapUrl(lat, lng),
      lat,
      lng,
    };
  };
  const startLoc = loc("start");
  const finishLoc = loc("finish");
  const dateText = (() => {
    try {
      return format(new Date(finishedAt || submittedAt), "EEE d MMM");
    } catch {
      return "";
    }
  })();
  const cleanerFirst = cleanerName.trim().split(/\s+/)[0] || "the cleaner";

  // ---------- photos by room ----------
  const walkBy = simple.walk_by_room ?? {};
  const afterBy = simple.after_by_room ?? {};
  const hasRoomPhotos = Object.keys(walkBy).length > 0 || Object.keys(afterBy).length > 0;
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [phase, setPhase] = useState<Record<string, "before" | "after">>({});
  const [lightbox, setLightbox] = useState<string | null>(null);

  const roomExceptionChips = (roomId: string, groupId?: string) => {
    const list = items.filter((it) => {
      const r = roomOf(it);
      if (r?.id !== roomId) return false;
      if (groupId === undefined) return true;
      if (groupId === WHOLE_ROOM_KEY) return !it.photo_group_id || !groups.some((g) => g.id === it.photo_group_id);
      return it.photo_group_id === groupId;
    });
    const out: { label: string; kind: "missing" | "broken" }[] = [];
    if (groupId === undefined) {
      const m = list.filter((i) => statusOf(i.id) === "missing").length;
      const b = list.filter((i) => statusOf(i.id) === "broken").length;
      if (m) out.push({ label: `${m} missing`, kind: "missing" });
      if (b) out.push({ label: `${b} broken`, kind: "broken" });
    } else {
      for (const i of list) {
        const s = statusOf(i.id);
        if (s !== "here") out.push({ label: `${i.name} ${s}`, kind: s });
      }
    }
    return out;
  };

  const roomModels: RoomBlockModel[] = rooms.map((room) => {
    const shots: RoomShots | undefined = walkBy[room.id];
    const after: RoomShots | undefined = afterBy[room.id];
    const roomGroups: PhotoGroup[] = groupsForRoom(groups, room.id);
    const guided = shots?.shots ?? {};
    const roomItems = items.filter((it) => roomOf(it)?.id === room.id);
    const groupIds = new Set(roomGroups.map((g) => g.id));
    const looseCount = roomItems.filter((i) => !i.photo_group_id || !groupIds.has(i.photo_group_id)).length;
    const hasGuided = Object.keys(guided).length > 0;
    const guidedCount = Object.keys(guided).length;
    const extras = shots?.extras ?? [];
    const wideAfterCount = after?.photo_urls?.length ?? 0;
    const whole = guided[WHOLE_ROOM_KEY];
    const skip = shots?.skip ?? after?.skip;
    const metaParts: string[] = [];
    if (hasGuided) {
      metaParts.push(`${guidedCount} guided shot${guidedCount === 1 ? "" : "s"} before${extras.length ? ` + ${extras.length} extra` : ""}`);
    } else if (shots?.photo_urls?.length) {
      metaParts.push(`${shots.photo_urls.length} photo${shots.photo_urls.length === 1 ? "" : "s"} before`);
    }
    metaParts.push(`${wideAfterCount} wide shot${wideAfterCount === 1 ? "" : "s"} after`);
    return {
      id: room.id,
      name: room.name,
      meta: metaParts.join(" · "),
      chips: roomExceptionChips(room.id),
      skipText: skip ? SKIP_TEXT[skip] : null,
      groups: roomGroups
        .filter((g) => guided[g.id])
        .map((g) => ({
          key: g.id,
          name: g.name,
          url: guided[g.id]?.url ?? null,
          time: hhmm(guided[g.id]?.retaken_at ?? guided[g.id]?.at),
          itemsText: itemsCountText(roomItems.filter((i) => i.photo_group_id === g.id).length),
          chips: roomExceptionChips(room.id, g.id),
          noRef: !g.reference_photo_url,
        })),
      wideTitleBefore: `Shot 1 · whole room${extras.length ? " and extra photos" : ""}${looseCount ? ` · covers ${itemsCountText(looseCount)} with no group` : ""}`,
      wideBefore: hasGuided
        ? [
            ...(whole ? [{ url: whole.url, caption: `Whole room · ${hhmm(whole.retaken_at ?? whole.at)}` }] : []),
            ...extras.map((e) => ({ url: e.url, caption: `Extra photo · ${hhmm(e.at)}` })),
          ]
        : [],
      wideAfter: (after?.photo_urls ?? []).map((url, i) => ({ url, caption: `Wide shot ${i + 1}` })),
      legacyBefore: hasGuided ? [] : (shots?.photo_urls ?? []),
    };
  });

  // ---------- unit items check ----------
  const [seen, setSeen] = useState<Set<string>>(() => new Set());
  const [busyId, setBusyId] = useState<string | null>(null);

  type Entry = { kind: "unit" | "supply" | "appliance"; id: string; name: string; room: string; photo: string | null };
  const unitEntry = (item: PropertyUnitItem): Entry => ({ kind: "unit", id: item.id, name: item.name, room: item.room || "Unit", photo: item.photo_url ?? null });
  const markOf = (e: Pick<Entry, "kind" | "id">) =>
    marks.find((m) =>
      e.kind === "unit" ? m.unit_item_id === e.id : e.kind === "supply" ? m.inventory_item_id === e.id : m.appliance_id === e.id,
    );

  /** Mark one item Here / Missing / Broken. `force` also records a first "Here" (a check, not a change). */
  const setEntryStatus = async (e: Entry, status: ItemStatus, force = false): Promise<boolean> => {
    if (!cleaningTaskId) return false;
    const existing = markOf(e);
    const current: ItemStatus = e.kind === "unit" ? statusOf(e.id) : existing?.status ?? "here";
    if (current === status && !(force && !existing)) return true;
    const flag = e.kind === "unit" ? reports.find((r) => r.unit_item_id === e.id && isCleanerFlag(r)) ?? null : null;
    setBusyId(`${e.kind}:${e.id}`);
    try {
      let issueReportId = existing?.issue_report_id ?? flag?.id ?? null;
      if ((status === "missing" || status === "broken") && !issueReportId && cleaningTask?.cleaner_id) {
        try {
          const report = await createReport.mutateAsync({
            property_id: propertyId,
            cleaning_task_id: cleaningTaskId,
            cleaner_id: cleaningTask.cleaner_id,
            room_name: e.room,
            response: status === "broken" ? "needs_repair" : "no",
            notes: `${status === "broken" ? "Broken" : "Missing"}: ${e.name}`,
            photos: e.photo ? [e.photo] : [],
            task_description: status === "broken" ? `Broken: ${e.name}` : `Missing: ${e.name}`,
            checklist_name: OWNER_CHECK_LIST_NAME,
            unit_item_id: e.kind === "unit" ? e.id : null,
          });
          issueReportId = report.id;
        } catch {
          /* the mark still saves */
        }
      } else if (issueReportId) {
        // Keep the flag and the check in step: Here closes it, Missing / Broken opens it again.
        await syncUnitCheckIssueReport({ reportId: issueReportId, status, itemName: e.name });
      }
      await upsert.mutateAsync({
        cleaningTaskId,
        itemKind: e.kind,
        unitItemId: e.id,
        status,
        issueReportId: issueReportId ?? undefined,
      });
      setSeen((prev) => new Set(prev).add(e.id));
      void queryClient.invalidateQueries({ queryKey: ["maintenance-reports-for-task", cleaningTaskId] });
      void queryClient.invalidateQueries({ queryKey: ["maintenance-reports", propertyId] });
      void queryClient.invalidateQueries({ queryKey: ["reports-unit-check-counts", propertyId] });
      void queryClient.invalidateQueries({ queryKey: ["reports-unit-check-marks", propertyId] });
      return true;
    } catch (err) {
      // Supabase errors are plain objects, not Error instances: read the message directly.
      const msg = (err as { message?: string } | null)?.message;
      toast({
        title: "Couldn’t save",
        description: /appliance_id|inventory_item_id|schema cache/i.test(msg ?? "")
          ? "The database needs one update for supplies and appliances (check marks). Run apply_check_marks_all_items.sql in Supabase."
          : msg,
        variant: "destructive",
      });
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const setStatus = (item: PropertyUnitItem, status: ItemStatus) => setEntryStatus(unitEntry(item), status);

  const saveNote = async (item: PropertyUnitItem, note: string) => {
    if (!cleaningTaskId) return;
    try {
      await upsert.mutateAsync({ cleaningTaskId, unitItemId: item.id, status: statusOf(item.id), note });
    } catch (err) {
      toast({ title: "Couldn’t save the note", description: err instanceof Error ? err.message : undefined, variant: "destructive" });
    }
  };

  // ---------- pairs by spot ----------
  const hasGuidedRoom = (id: string) => Object.keys(walkBy[id]?.shots ?? {}).length > 0 || Object.keys(afterBy[id]?.shots ?? {}).length > 0;
  const pairRooms: PairRoom[] = rooms
    .filter((room) => hasGuidedRoom(room.id))
    .map((room) => {
      const before = walkBy[room.id];
      const after = afterBy[room.id];
      const roomGroups: PhotoGroup[] = groupsForRoom(groups, room.id);
      const groupIds = new Set(roomGroups.map((g) => g.id));
      const invRoomIds = new Set(invRoomsRaw.filter((r) => sameRoom(r.name, room.name)).map((r) => r.id));
      const entries: { entry: Entry; groupId: string | null }[] = [
        ...items
          .filter((it) => roomOf(it)?.id === room.id)
          .map((it) => ({ entry: unitEntry(it), groupId: it.photo_group_id ?? null })),
        ...invItems
          .filter((i) => i.room_id && invRoomIds.has(i.room_id))
          .map((i) => ({
            entry: { kind: "supply" as const, id: i.id, name: i.item_name, room: room.name, photo: null },
            groupId: i.photo_group_id ?? null,
          })),
        ...(appliancesRaw as { id: string; name?: string | null; location?: string | null; photo_group_id?: string | null }[])
          .filter((a) => sameRoom(a.location || "", room.name))
          .map((a) => ({
            entry: { kind: "appliance" as const, id: a.id, name: a.name || "Appliance", room: room.name, photo: null },
            groupId: a.photo_group_id ?? null,
          })),
      ];
      const toItem = (e: Entry): PairItem => {
        const mark = markOf(e);
        const flagged = e.kind === "unit" && flagByItem.has(e.id);
        return {
          key: `${e.kind}:${e.id}`,
          kind: e.kind,
          id: e.id,
          name: e.name,
          status: e.kind === "unit" ? statusOf(e.id) : mark?.status ?? "here",
          checked: !!mark || flagged,
        };
      };
      const spotDefs: { key: string; name: string; sub: string; ids: (g: string | null) => boolean }[] = [
        {
          key: WHOLE_ROOM_KEY,
          name: "Whole room",
          sub: "Out in the open",
          ids: (g) => !g || !groupIds.has(g),
        },
        ...roomGroups.map((g) => ({ key: g.id, name: g.name, sub: "", ids: (gid: string | null) => gid === g.id })),
      ];
      const spots = spotDefs
        .map((def) => {
          const list = entries.filter((x) => def.ids(x.groupId)).map((x) => toItem(x.entry));
          const b = before?.shots?.[def.key];
          const a =
            after?.shots?.[def.key] ??
            (def.key === WHOLE_ROOM_KEY && !after?.shots && after?.photo_urls?.[0] ? { url: after.photo_urls[0], at: "" } : undefined);
          return {
            key: def.key,
            name: def.name,
            sub: def.key === WHOLE_ROOM_KEY ? def.sub : `${list.length} item${list.length === 1 ? "" : "s"}`,
            beforeUrl: b?.url ?? null,
            beforeTime: hhmm(b?.retaken_at ?? b?.at),
            afterUrl: a?.url ?? null,
            afterTime: hhmm((a as { retaken_at?: string; at?: string } | undefined)?.retaken_at ?? (a as { at?: string } | undefined)?.at),
            items: list,
          };
        })
        .filter((sp) => sp.beforeUrl || sp.afterUrl || sp.items.length > 0);
      const skip = before?.skip ?? after?.skip;
      return { id: room.id, name: room.name, skipText: skip ? SKIP_TEXT[skip] : null, spots };
    });
  const cycleItem = (it: PairItem, next: PairStatus) => {
    const e = pairEntry(it);
    if (e) void setEntryStatus(e, next, true);
  };
  const pairEntry = (it: PairItem): Entry | null => {
    if (it.kind === "unit") {
      const u = items.find((x) => x.id === it.id);
      return u ? unitEntry(u) : null;
    }
    return { kind: it.kind, id: it.id, name: it.name, room: "", photo: null };
  };
  const allHere = async (list: PairItem[]) => {
    for (const it of list) {
      const e = pairEntry(it);
      if (e && !(await setEntryStatus(e, "here", true))) break;
    }
  };

  const groupOfItem = (it: PropertyUnitItem): PhotoGroup | null => groups.find((g) => g.id === it.photo_group_id) ?? null;
  const exceptionIds = items.filter((it) => statusOf(it.id) !== "here" || seen.has(it.id)).map((it) => it.id);
  const exceptionModels: { item: PropertyUnitItem; model: ExceptionRowModel }[] = exceptionIds.map((id) => {
    const it = items.find((i) => i.id === id)!;
    const room = roomOf(it);
    const g = groupOfItem(it);
    const status = statusOf(id);
    const flag = reports.find((r) => r.unit_item_id === id && isCleanerFlag(r)) ?? null;
    const shotKey = g ? g.id : WHOLE_ROOM_KEY;
    const shot = room ? walkBy[room.id]?.shots?.[shotKey] : undefined;
    const mark = marks.find((m) => m.unit_item_id === id);
    let src: string;
    let srcTone: ExceptionRowModel["srcTone"];
    if (flag && status !== "here") {
      src = `Maintenance flag · raised by ${cleanerFirst} at ${hhmm(flag.created_at)}`;
      srcTone = "flag";
    } else if (flag) {
      src = "Flag closed · you marked it Here";
      srcTone = "closed";
    } else if (status === "here") {
      src = "Back to Here · moves to All here when you leave";
      srcTone = "here";
    } else {
      src = `Marked ${status === "missing" ? "Missing" : "Broken"} by you`;
      srcTone = status;
    }
    const cover = room ? roomCover[room.id]?.cover_photo_url ?? null : null;
    const refUrl = g ? g.reference_photo_url ?? null : cover ?? it.photo_url ?? null;
    return {
      item: it,
      model: {
        id,
        label: itemLabel(it.name, it.quantity),
        where: `${room?.name ?? it.room ?? "Other"} · ${g ? g.name : "Whole room photo"}`,
        status,
        src,
        srcTone,
        beforeUrl: shot?.url ?? null,
        beforeCaption: shot ? `Before photo · ${hhmm(shot.retaken_at ?? shot.at)}` : "No before photo",
        refUrl,
        refNone: !refUrl,
        flagUrl: flag?.photos?.[0] ?? null,
        flagCaption: flag ? `${cleanerFirst}’s flag photo · ${hhmm(flag.created_at)}` : "",
        note: mark?.note ?? "",
      },
    };
  });
  const restItems = items.filter((it) => !exceptionIds.includes(it.id));
  const [allOpen, setAllOpen] = useState(false);

  // ---------- maintenance ----------
  const maint = counts.maintenance;
  const flags = reports.filter((r) => isCleanerFlag(r));

  // ---------- rating / test ----------
  const canRate =
    !!cleaningTask &&
    cleaningTask.checklist_completed === true &&
    cleaningTask.inventory_completed === true &&
    cleaningTask.rating == null;
  const arrivalKeysUrl = simple.keys_photo_url || null;
  const dropKeysUrl =
    simple.keys_drop_photo_url ||
    (simple.keys_photo_url
      ? cleaningTask?.closeout_keys_photo_url && cleaningTask.closeout_keys_photo_url !== simple.keys_photo_url
        ? cleaningTask.closeout_keys_photo_url
        : null
      : cleaningTask?.closeout_keys_photo_url || null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const pretendLine = testSetupLine(cleaningTask?.test_setup);
  const confirmDeleteTest = async () => {
    if (!cleaningTaskId) return;
    setDeleting(true);
    try {
      await deleteTestClean(cleaningTaskId);
      setDeleteOpen(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["cleaning-submissions"] }),
        queryClient.invalidateQueries({ queryKey: ["reports-completed-tasks", propertyId] }),
        queryClient.invalidateQueries({ queryKey: ["maintenance-reports", propertyId] }),
        queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", propertyId] }),
        queryClient.invalidateQueries({ queryKey: ["inventory-reorders"] }),
      ]);
      toast({ title: "Test clean deleted" });
      onTestDeleted?.();
    } catch (err) {
      toast({ title: "Couldn’t delete the test clean", description: err instanceof Error ? err.message : "Try again.", variant: "destructive" });
    } finally {
      setDeleting(false);
    }
  };

  const toneClass = { green: "text-[#15776d]", amber: "text-[#b45309]", red: "text-[#b4453a]" } as const;
  const dotClass = { green: "bg-[#24a89a]", amber: "bg-[#d97706]", red: "bg-[#b4453a]" } as const;
  const beforeUrls = collectSimplePhotoUrls(simple, "before");
  const afterUrls = collectSimplePhotoUrls(simple, "after");

  return (
    <div className="flex flex-col gap-6" data-tour="tour-job-evidence">
      <button type="button" onClick={onBack} className="inline-flex w-fit items-center gap-1.5 text-[14px] font-bold text-[#64748b] hover:text-[#24a89a]">
        <ChevronLeft className="h-4 w-4" strokeWidth={2.4} />
        All reports
      </button>

      {photosPending > 0 ? (
        <div className="flex flex-col gap-1.5 rounded-[20px] bg-white px-[18px] py-3.5">
          <span className="inline-flex w-fit rounded-full bg-[#eef2f5] px-3 py-1 text-[12.5px] font-bold text-[#64748b]">
            Waiting to send from the cleaner’s phone
          </span>
          <div className="text-[14px] font-medium leading-snug text-[#475569]">
            {photosPending} photo{photosPending === 1 ? " is" : "s are"} still on the cleaner’s phone. They appear here on their own when it’s back in signal.
          </div>
        </div>
      ) : null}

      {/* Header */}
      <SectionCard>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-2.5">
            <div className="flex flex-wrap items-center gap-2.5">
              <div className="text-[26px] font-bold leading-tight tracking-[-0.5px] text-[#0f1720] max-sm:text-[22px]">
                {propertyLabel} · Clean on {dateText}
              </div>
              {isTest ? (
                <span className="inline-flex h-[26px] items-center rounded-full bg-[#0f1720] px-3 text-[12.5px] font-bold text-white">
                  Test clean{(cleaningTask?.test_setup as { ended_early?: boolean } | null)?.ended_early ? " · ended early" : ""}
                </span>
              ) : null}
            </div>
            <div className="flex items-center gap-2.5">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#e2eeed] text-[12.5px] font-bold text-[#15776d]">
                {cleanerFirst[0]?.toUpperCase() ?? "?"}
              </span>
              <span className="text-[15px] font-bold text-[#0f1720]">{cleanerName}</span>
              <span className="text-[13px] font-medium text-[#8494a2]">Cleaner</span>
            </div>
          </div>
          <Pill kind={counts.clear ? "here" : "grey"}>{counts.clear ? "Done" : "Review"}</Pill>
        </div>

        <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-6 gap-y-2 text-[15px]">
          <div className="font-medium text-[#8494a2]">Clean took</div>
          <div className="text-[#475569]">
            <span className="font-bold text-[#0f1720]">{tookMin != null ? formatDurationWords(tookMin).replace(" hours", " h").replace(" hour", " h").replace(" minutes", " min").replace(" minute", " min") : "—"}</span>
            {startedAt ? ` · started ${hhmm(startedAt)}` : ""}
            {finishedAt ? `, finished ${hhmm(finishedAt)}` : ""}
          </div>
          <div className="font-medium text-[#8494a2]">Start location</div>
          <div className={cn("flex items-center gap-2 font-bold", toneClass[startLoc.tone])}>
            <span className={cn("h-2 w-2 rounded-full", dotClass[startLoc.tone])} />
            {startLoc.href ? (
              <a href={startLoc.href} target="_blank" rel="noopener noreferrer" className="hover:underline">
                {startLoc.text}
              </a>
            ) : (
              startLoc.text
            )}
          </div>
          <div className="font-medium text-[#8494a2]">Finish location</div>
          <div className={cn("flex items-center gap-2 font-bold", toneClass[finishLoc.tone])}>
            <span className={cn("h-2 w-2 rounded-full", dotClass[finishLoc.tone])} />
            {finishLoc.href ? (
              <a href={finishLoc.href} target="_blank" rel="noopener noreferrer" className="hover:underline">
                {finishLoc.text}
              </a>
            ) : (
              finishLoc.text
            )}
          </div>
        </div>

        {startLoc.lat != null || finishLoc.lat != null ? (
          <div className="flex flex-col gap-2.5">
            <div className="text-[15px] font-bold text-[#0f1720]">Where {cleanerFirst} was</div>
            <div className="grid gap-3 md:grid-cols-2">
              <MapTile label="Started" time={hhmm(startedAt)} lat={startLoc.lat} lng={startLoc.lng} text={startLoc.text} tone={startLoc.tone} />
              <MapTile label="Finished" time={hhmm(finishedAt)} lat={finishLoc.lat} lng={finishLoc.lng} text={finishLoc.text} tone={finishLoc.tone} />
            </div>
          </div>
        ) : null}

        <VerdictLine clear={counts.clear} text={verdictLine(counts, items.length)} maintOk={maint.complete} />
        {isTest ? (
          <div className="flex flex-col gap-1 text-[14px] font-medium text-[#475569]">
            <span>Not counted in stats or billing.</span>
            {pretendLine ? <span className="font-bold text-[#0f1720]">Pretend turnover · {pretendLine}</span> : null}
          </div>
        ) : null}
      </SectionCard>

      {/* Photos by room */}
      <SectionCard>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div className="text-[19px] font-medium tracking-[-0.2px] text-[#0f1720]">Photos by room</div>
          <div className="text-[13px] font-medium text-[#8494a2]">
            {pairRooms.length > 0 ? "Before left, after right · tap an item to change it" : "Before: guided item shots · After: wide shots of every room"}
          </div>
        </div>
        {!!cleaningTask?.started_at && !cleaningTask?.completed_at && pairRooms.length > 0 ? (
          <div className="flex items-center gap-2.5 rounded-[14px] bg-[#f4f7f9] px-4 py-3">
            <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-[#24a89a]" />
            <div className="text-[14.5px] font-bold text-[#0f1720]">
              Clean in progress{startedAt ? ` · started ${hhmm(startedAt)}` : ""}
              <span className="ml-1.5 font-medium text-[#64748b]">After photos appear as {cleanerFirst} takes them</span>
            </div>
          </div>
        ) : null}
        {pairRooms.length > 0 ? (
          <ReportPairs
            rooms={pairRooms}
            inProgress={!!cleaningTask?.started_at && !cleaningTask?.completed_at}
            busyKey={busyId}
            onCycle={cycleItem}
            onAllHere={(list) => void allHere(list)}
          />
        ) : null}
        {hasRoomPhotos && roomModels.some((rm) => !hasGuidedRoom(rm.id)) ? (
          <div className="flex flex-col">
            {roomModels
              .filter((rm) => !hasGuidedRoom(rm.id))
              .map((rm) => (
                <RoomBlock
                  key={rm.id}
                  room={rm}
                  open={!!open[rm.id]}
                  phase={phase[rm.id] ?? "before"}
                  onToggle={() => setOpen((o) => ({ ...o, [rm.id]: !o[rm.id] }))}
                  onPhase={(p) => {
                    setPhase((s) => ({ ...s, [rm.id]: p }));
                    setOpen((o) => ({ ...o, [rm.id]: true }));
                  }}
                  onOpenPhoto={setLightbox}
                />
              ))}
          </div>
        ) : null}
        {!hasRoomPhotos ? (
          <div className="grid gap-4 md:grid-cols-2">
            {([["Before", beforeUrls], ["After", afterUrls]] as const).map(([title, urls]) => (
              <div key={title} className="flex flex-col gap-2">
                <div className="text-[15px] font-bold text-[#0f1720]">
                  {title} · {urls.length} photo{urls.length === 1 ? "" : "s"}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {urls.map((u) => (
                    <button key={u} type="button" onClick={() => setLightbox(u)} className="aspect-square overflow-hidden rounded-[10px] bg-[#e8eef2]">
                      <img src={u} alt="" className="h-full w-full object-cover" loading="lazy" />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </SectionCard>

      {/* Unit items that need a look (the full check lives on the chips above) */}
      {exceptionModels.length > 0 ? (
        <SectionCard>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div className="text-[19px] font-medium tracking-[-0.2px] text-[#0f1720]">Items that need a look</div>
            <div className="text-[13px] font-medium text-[#8494a2]">
              {exceptionModels.length} item{exceptionModels.length === 1 ? "" : "s"}
            </div>
          </div>
          <div className="flex flex-col gap-3">
            {exceptionModels.map(({ item, model }) => (
              <ExceptionRow
                key={model.id}
                row={model}
                busy={busyId === `unit:${model.id}`}
                onStatus={(s2) => void setStatus(item, s2)}
                onNote={(v) => void saveNote(item, v)}
                onOpenPhoto={setLightbox}
              />
            ))}
          </div>
        </SectionCard>
      ) : null}

      {/* Maintenance */}
      <SectionCard>
        <div className="text-[19px] font-medium tracking-[-0.2px] text-[#0f1720]">Maintenance</div>
        {maint.total === 0 ? (
          <div className="text-[15px] font-medium text-[#64748b]">No maintenance checks were due on this clean.</div>
        ) : maint.complete ? (
          <div className="flex items-center gap-2.5 text-[16px] font-bold text-[#15776d]">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#24a89a]">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12.5l4.5 4.5L19 7.5" />
              </svg>
            </span>
            {cleanerFirst} completed all {maint.total} maintenance checks.
          </div>
        ) : (
          <div className="flex flex-col gap-2.5 rounded-[16px] bg-[#fdf3e3] p-4">
            <div className="text-[17px] font-bold text-[#0f1720]">
              Maintenance checks not completed · {maint.done} of {maint.total} done
            </div>
            <div className="text-[14px] font-medium text-[#5a4a2a]">These were on the job but {cleanerFirst} finished without doing them.</div>
            {maint.missed.map((m) => (
              <div key={m} className="flex items-center justify-between gap-3 rounded-[12px] bg-white px-3.5 py-2.5">
                <div className="text-[15px] font-bold text-[#0f1720]">{m}</div>
                <Pill kind="broken" small>
                  Not done
                </Pill>
              </div>
            ))}
          </div>
        )}
        {flags.length > 0 ? (
          <div className="flex flex-col gap-2.5">
            {flags.map((f) => {
              const it = f.unit_item_id ? items.find((i) => i.id === f.unit_item_id) : null;
              const stillOpen = isFlagOpen(f);
              return (
                <div key={f.id} className="flex flex-wrap items-center gap-3.5 rounded-[16px] border-[1.5px] border-[#e6edf1] p-3">
                  <div className="h-[64px] w-[84px] shrink-0 overflow-hidden rounded-[11px] bg-[#e8eef2]">
                    {f.photos?.[0] ? (
                      <button type="button" className="h-full w-full" onClick={() => setLightbox(f.photos![0])}>
                        <img src={f.photos[0]} alt="" className="h-full w-full object-cover" />
                      </button>
                    ) : null}
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <div className="truncate text-[16px] font-bold text-[#0f1720]">
                      {it
                        ? `${itemLabel(it.name, it.quantity)} · ${f.task_description?.startsWith("Missing") ? "Missing" : "Broken"}`
                        : f.task_description || f.notes || "Issue"}
                    </div>
                    <div className="text-[13px] font-medium text-[#64748b]">
                      {f.room_name} · raised at {hhmm(f.created_at)}
                    </div>
                    <div className={cn("text-[13px] font-bold", it ? (stillOpen ? "text-[#8a5a00]" : "text-[#15776d]") : "text-[#8494a2]")}>
                      {it
                        ? stillOpen
                          ? "Also marked in the unit check"
                          : "Closed · you marked it Here in the unit check"
                        : "Not linked to a unit item"}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate(`/properties/${propertyId}?tab=maintenance&focusIssueId=${f.id}`)}
                    className="h-10 shrink-0 rounded-xl border-[1.5px] border-[#d7e0e6] bg-white px-3.5 text-[14px] font-bold text-[#0f1720]"
                  >
                    Open in Issues
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-[14.5px] font-medium text-[#8494a2]">No maintenance flags raised.</div>
        )}
      </SectionCard>

      {/* Inventory: only when something is low or empty */}
      {lowRows.length > 0 ? <LowInventory rows={lowRows} onOpenInventory={onGoToInventory} /> : null}

      {/* Keys and arrival checklist */}
      {arrivalKeysUrl || dropKeysUrl ? (
        <SectionCard>
          <div className="flex items-center justify-between gap-3">
            <div className="text-[19px] font-medium tracking-[-0.2px] text-[#0f1720]">Keys</div>
            {dropKeysUrl ? (
              <Pill kind="here" small>
                Left in spot
              </Pill>
            ) : (
              <Pill kind="broken" small>
                No dropoff photo
              </Pill>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 md:max-w-[520px]">
            {([["Arrival keys", arrivalKeysUrl], ["Keys left", dropKeysUrl]] as const).map(([label, url]) => (
              <div key={label} className="flex flex-col gap-1.5">
                <button
                  type="button"
                  disabled={!url}
                  onClick={() => url && setLightbox(url)}
                  className={cn("aspect-[4/3] overflow-hidden rounded-[12px]", url ? "bg-[#e8eef2]" : "border-2 border-dashed border-[#e4c99b] bg-[#fffaf2]")}
                >
                  {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : null}
                </button>
                <div className="text-[13px] font-bold text-[#475569]">{label}</div>
              </div>
            ))}
          </div>
        </SectionCard>
      ) : null}
      <ArrivalAnswersSection arrivalTasks={cleaningTask?.arrival_tasks} entryWalkthroughState={cleaningTask?.entry_walkthrough_state} />

      {cleaningTaskId && !isTest ? (
        <div className="flex flex-col gap-4">
          <CleanerRatingCard
            taskId={cleaningTaskId}
            cleanerName={cleanerName}
            existingRating={cleaningTask?.rating ?? null}
            existingNote={cleaningTask?.rating_note ?? null}
            ratedAt={cleaningTask?.rated_at ?? null}
            canRate={canRate}
          />
          <ShareWithGuest cleaningTaskId={cleaningTaskId} propertyId={propertyId} compact />
        </div>
      ) : null}

      {isTest && cleaningTaskId ? (
        <div className="flex flex-wrap items-center gap-4 border-t-[1.5px] border-[#e2e9ee] pt-4">
          <div className="min-w-0 flex-1 text-[14px] font-medium text-[#64748b]">
            Delete removes the report, its photos, and its lines in Issues and the buy list.
          </div>
          <button
            type="button"
            onClick={() => setDeleteOpen(true)}
            className="flex h-[52px] items-center rounded-[14px] border-[1.5px] border-[#efc6c0] bg-white px-[22px] text-[16px] font-bold text-[#b4453a] hover:bg-[#fdf1ef]"
          >
            Delete test clean
          </button>
        </div>
      ) : null}

      {lightbox ? (
        <button type="button" className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" className="max-h-[90vh] max-w-[92vw] rounded-xl object-contain" />
        </button>
      ) : null}

      {deleteOpen ? (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-[rgba(15,23,32,0.45)] sm:items-center">
          <div className="flex w-full flex-col gap-3.5 rounded-t-[22px] bg-white p-6 shadow-[0_24px_60px_rgba(15,23,32,0.25)] sm:w-[460px] sm:rounded-[22px]">
            <div className="text-[23px] font-bold tracking-[-0.4px]">Delete this test clean?</div>
            <div className="text-[15.5px] font-medium leading-relaxed text-[#475569]">
              Its photos, counts and problems go too, including the ones in Issues and the buy list. Real stats and billing were never touched.
            </div>
            <div className="mt-1.5 flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
              <button
                type="button"
                disabled={deleting}
                onClick={() => setDeleteOpen(false)}
                className="flex h-[52px] items-center justify-center rounded-[14px] border-[1.5px] border-[#d7e0e6] px-[22px] text-[16px] font-bold"
              >
                Keep it
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={() => void confirmDeleteTest()}
                className="flex h-[52px] items-center justify-center rounded-[14px] bg-[#b4453a] px-[22px] text-[16px] font-bold text-white"
              >
                {deleting ? <Loader2 className="h-5 w-5 animate-spin" /> : "Delete test clean"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
